import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Copy,
  Check,
  Plus,
  Trash2,
  Play,
  Download,
  ExternalLink,
  Zap,
  Globe,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  Webhook,
  Code2,
  BookOpen,
  Settings,
  Database,
  Users,
  Calendar,
  MapPin,
  CreditCard,
  BarChart3,
  Bell,
  Cake,
  TrendingDown,
  Building2,
  ClipboardList,
  Megaphone,
  PartyPopper
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Complete API endpoints organized by category for Bubble.io
const bubbleEndpoints = [
  {
    category: 'Autenticación',
    icon: Users,
    color: 'bg-blue-500',
    description: 'Endpoints para autenticar usuarios y gestionar sesiones',
    endpoints: [
      {
        name: 'Login con PIN',
        method: 'POST',
        path: '/api/auth/login/pin',
        description: 'Autenticar usuario con nombre de restaurante y PIN',
        auth: false,
        body: {
          restaurant_name: 'string',
          pin: 'string (4 dígitos)'
        },
        response: {
          access_token: 'string',
          token_type: 'bearer',
          user: 'object'
        }
      },
      {
        name: 'Establecer PIN',
        method: 'POST',
        path: '/api/auth/set-pin',
        description: 'Establecer PIN para el usuario (primer login)',
        auth: true,
        body: {
          pin: 'string (4 dígitos)'
        },
        response: {
          message: 'string'
        }
      },
      {
        name: 'Obtener Usuario Actual',
        method: 'GET',
        path: '/api/auth/me',
        description: 'Obtener información del usuario autenticado',
        auth: true,
        response: {
          id: 'string',
          name: 'string',
          email: 'string',
          role: 'string',
          restaurant_id: 'string'
        }
      }
    ]
  },
  {
    category: 'Restaurante',
    icon: Building2,
    color: 'bg-orange-500',
    description: 'Gestión de configuración del restaurante',
    endpoints: [
      {
        name: 'Obtener Restaurante',
        method: 'GET',
        path: '/api/restaurant',
        description: 'Obtener configuración completa del restaurante',
        auth: true,
        response: {
          id: 'string',
          name: 'string',
          address: 'string',
          phone: 'string',
          email: 'string',
          settings: 'object'
        }
      },
      {
        name: 'Actualizar Restaurante',
        method: 'PATCH',
        path: '/api/restaurant',
        description: 'Actualizar configuración del restaurante',
        auth: true,
        body: {
          name: 'string (opcional)',
          address: 'string (opcional)',
          phone: 'string (opcional)',
          settings: 'object (opcional)'
        }
      },
      {
        name: 'Subir Logo',
        method: 'POST',
        path: '/api/restaurant/logo',
        description: 'Subir logo del restaurante (multipart/form-data)',
        auth: true,
        body: {
          file: 'File (imagen)'
        }
      }
    ]
  },
  {
    category: 'Ubicaciones (Multi-Location)',
    icon: MapPin,
    color: 'bg-purple-500',
    description: 'Gestión de múltiples ubicaciones para cadenas',
    endpoints: [
      {
        name: 'Listar Ubicaciones',
        method: 'GET',
        path: '/api/locations',
        description: 'Obtener todas las ubicaciones accesibles',
        auth: true,
        response: '[array de ubicaciones]'
      },
      {
        name: 'Crear Ubicación',
        method: 'POST',
        path: '/api/locations',
        description: 'Crear nueva sucursal',
        auth: true,
        body: {
          name: 'string',
          address: 'string',
          phone: 'string'
        }
      },
      {
        name: 'Cambiar Ubicación Activa',
        method: 'POST',
        path: '/api/locations/switch/{location_id}',
        description: 'Cambiar a otra ubicación',
        auth: true
      },
      {
        name: 'Actualizar Ubicación',
        method: 'PATCH',
        path: '/api/locations/{location_id}',
        description: 'Actualizar datos de ubicación',
        auth: true,
        body: {
          name: 'string (opcional)',
          address: 'string (opcional)'
        }
      },
      {
        name: 'Eliminar Ubicación',
        method: 'DELETE',
        path: '/api/locations/{location_id}',
        description: 'Eliminar una ubicación',
        auth: true
      }
    ]
  },
  {
    category: 'Mesas',
    icon: Database,
    color: 'bg-green-500',
    description: 'Gestión de mesas del restaurante',
    endpoints: [
      {
        name: 'Listar Mesas',
        method: 'GET',
        path: '/api/tables',
        description: 'Obtener todas las mesas',
        auth: true,
        response: '[array de mesas]'
      },
      {
        name: 'Crear Mesa',
        method: 'POST',
        path: '/api/tables',
        description: 'Crear nueva mesa',
        auth: true,
        body: {
          number: 'string',
          capacity: 'number',
          zone_id: 'string (opcional)',
          x: 'number (posición)',
          y: 'number (posición)'
        }
      },
      {
        name: 'Obtener Mesa',
        method: 'GET',
        path: '/api/tables/{table_id}',
        description: 'Obtener mesa específica',
        auth: true
      },
      {
        name: 'Actualizar Mesa',
        method: 'PATCH',
        path: '/api/tables/{table_id}',
        description: 'Actualizar datos de mesa',
        auth: true,
        body: {
          number: 'string (opcional)',
          capacity: 'number (opcional)',
          status: 'string (opcional)'
        }
      },
      {
        name: 'Cambiar Estado de Mesa',
        method: 'PATCH',
        path: '/api/tables/{table_id}/status',
        description: 'Cambiar estado: available, occupied, reserved, blocked',
        auth: true,
        body: {
          status: 'string'
        }
      },
      {
        name: 'Eliminar Mesa',
        method: 'DELETE',
        path: '/api/tables/{table_id}',
        description: 'Eliminar mesa',
        auth: true
      }
    ]
  },
  {
    category: 'Zonas',
    icon: MapPin,
    color: 'bg-cyan-500',
    description: 'Gestión de zonas del restaurante',
    endpoints: [
      {
        name: 'Listar Zonas',
        method: 'GET',
        path: '/api/zones',
        description: 'Obtener todas las zonas',
        auth: true
      },
      {
        name: 'Crear Zona',
        method: 'POST',
        path: '/api/zones',
        description: 'Crear nueva zona',
        auth: true,
        body: {
          name: 'string',
          color: 'string (hex)',
          capacity: 'number'
        }
      },
      {
        name: 'Actualizar Zona',
        method: 'PATCH',
        path: '/api/zones/{zone_id}',
        description: 'Actualizar zona',
        auth: true
      },
      {
        name: 'Eliminar Zona',
        method: 'DELETE',
        path: '/api/zones/{zone_id}',
        description: 'Eliminar zona',
        auth: true
      }
    ]
  },
  {
    category: 'Horarios',
    icon: Clock,
    color: 'bg-amber-500',
    description: 'Gestión de horarios disponibles',
    endpoints: [
      {
        name: 'Listar Horarios',
        method: 'GET',
        path: '/api/time-slots',
        description: 'Obtener horarios configurados',
        auth: true
      },
      {
        name: 'Crear Horario',
        method: 'POST',
        path: '/api/time-slots',
        description: 'Crear nuevo horario',
        auth: true,
        body: {
          day_of_week: 'number (0-6)',
          start_time: 'string (HH:MM)',
          end_time: 'string (HH:MM)',
          max_reservations: 'number'
        }
      },
      {
        name: 'Actualizar Horario',
        method: 'PATCH',
        path: '/api/time-slots/{slot_id}',
        description: 'Actualizar horario',
        auth: true
      },
      {
        name: 'Eliminar Horario',
        method: 'DELETE',
        path: '/api/time-slots/{slot_id}',
        description: 'Eliminar horario',
        auth: true
      }
    ]
  },
  {
    category: 'Reservaciones',
    icon: Calendar,
    color: 'bg-red-500',
    description: 'Gestión completa de reservaciones',
    endpoints: [
      {
        name: 'Listar Reservaciones',
        method: 'GET',
        path: '/api/reservations',
        description: 'Obtener reservaciones con filtros',
        auth: true,
        queryParams: {
          date: 'string (YYYY-MM-DD)',
          status: 'string',
          customer_id: 'string',
          page: 'number',
          limit: 'number'
        }
      },
      {
        name: 'Crear Reservación',
        method: 'POST',
        path: '/api/reservations',
        description: 'Crear nueva reservación',
        auth: true,
        body: {
          guest_name: 'string',
          guest_email: 'string',
          guest_phone: 'string',
          party_size: 'number',
          date: 'string (YYYY-MM-DD)',
          time: 'string (HH:MM)',
          notes: 'string (opcional)',
          table_id: 'string (opcional)'
        }
      },
      {
        name: 'Obtener Reservación',
        method: 'GET',
        path: '/api/reservations/{reservation_id}',
        description: 'Obtener detalle de reservación',
        auth: true
      },
      {
        name: 'Actualizar Reservación',
        method: 'PATCH',
        path: '/api/reservations/{reservation_id}',
        description: 'Actualizar reservación',
        auth: true,
        body: {
          party_size: 'number (opcional)',
          date: 'string (opcional)',
          time: 'string (opcional)',
          notes: 'string (opcional)'
        }
      },
      {
        name: 'Cambiar Estado',
        method: 'PATCH',
        path: '/api/reservations/{reservation_id}/status',
        description: 'Estados: pending, confirmed, seated, completed, cancelled, no_show',
        auth: true,
        body: {
          status: 'string'
        }
      },
      {
        name: 'Asignar Mesa',
        method: 'PATCH',
        path: '/api/reservations/{reservation_id}/assign-table',
        description: 'Asignar mesa a reservación',
        auth: true,
        body: {
          table_id: 'string'
        }
      },
      {
        name: 'Cancelar Reservación',
        method: 'DELETE',
        path: '/api/reservations/{reservation_id}',
        description: 'Cancelar reservación',
        auth: true
      },
      {
        name: 'Exportar a CSV',
        method: 'GET',
        path: '/api/reservations/export/csv',
        description: 'Exportar reservaciones a CSV',
        auth: true,
        queryParams: {
          start_date: 'string',
          end_date: 'string'
        }
      },
      {
        name: 'Exportar a PDF',
        method: 'GET',
        path: '/api/reservations/export/pdf',
        description: 'Exportar reservaciones a PDF',
        auth: true
      }
    ]
  },
  {
    category: 'Clientes (CRM)',
    icon: Users,
    color: 'bg-pink-500',
    description: 'Gestión de clientes y CRM',
    endpoints: [
      {
        name: 'Listar Clientes',
        method: 'GET',
        path: '/api/customers',
        description: 'Obtener todos los clientes',
        auth: true,
        queryParams: {
          search: 'string',
          page: 'number',
          limit: 'number'
        }
      },
      {
        name: 'Crear Cliente',
        method: 'POST',
        path: '/api/customers',
        description: 'Crear nuevo cliente',
        auth: true,
        body: {
          name: 'string',
          email: 'string',
          phone: 'string',
          birthday: 'string (YYYY-MM-DD)',
          notes: 'string (opcional)',
          tags: 'array (opcional)',
          allergies: 'array (opcional)'
        }
      },
      {
        name: 'Obtener Cliente',
        method: 'GET',
        path: '/api/customers/{customer_id}',
        description: 'Obtener perfil completo del cliente',
        auth: true
      },
      {
        name: 'Actualizar Cliente',
        method: 'PATCH',
        path: '/api/customers/{customer_id}',
        description: 'Actualizar datos del cliente',
        auth: true
      },
      {
        name: 'Eliminar Cliente',
        method: 'DELETE',
        path: '/api/customers/{customer_id}',
        description: 'Eliminar cliente',
        auth: true
      },
      {
        name: 'Enviar Cupón',
        method: 'POST',
        path: '/api/customers/{customer_id}/send-coupon',
        description: 'Enviar cupón de descuento al cliente',
        auth: true,
        body: {
          discount_type: 'string (percentage/fixed)',
          discount_value: 'number',
          valid_days: 'number'
        }
      },
      {
        name: 'Exportar Clientes CSV',
        method: 'GET',
        path: '/api/customers/export/csv',
        description: 'Exportar clientes a CSV',
        auth: true
      }
    ]
  },
  {
    category: 'Lista de Espera',
    icon: ClipboardList,
    color: 'bg-yellow-500',
    description: 'Gestión de lista de espera',
    endpoints: [
      {
        name: 'Listar Cola',
        method: 'GET',
        path: '/api/waitlist',
        description: 'Obtener lista de espera actual',
        auth: true
      },
      {
        name: 'Agregar a Lista',
        method: 'POST',
        path: '/api/waitlist',
        description: 'Agregar cliente a lista de espera',
        auth: true,
        body: {
          name: 'string',
          phone: 'string',
          party_size: 'number',
          notes: 'string (opcional)'
        }
      },
      {
        name: 'Notificar Disponibilidad',
        method: 'POST',
        path: '/api/waitlist/{entry_id}/notify',
        description: 'Enviar notificación de mesa disponible',
        auth: true
      },
      {
        name: 'Convertir a Reservación',
        method: 'POST',
        path: '/api/waitlist/{entry_id}/convert',
        description: 'Convertir entrada a reservación',
        auth: true,
        body: {
          table_id: 'string',
          time: 'string'
        }
      },
      {
        name: 'Eliminar de Lista',
        method: 'DELETE',
        path: '/api/waitlist/{entry_id}',
        description: 'Eliminar de lista de espera',
        auth: true
      }
    ]
  },
  {
    category: 'Eventos',
    icon: PartyPopper,
    color: 'bg-fuchsia-500',
    description: 'Gestión de eventos y venta de boletos',
    endpoints: [
      {
        name: 'Listar Eventos',
        method: 'GET',
        path: '/api/events',
        description: 'Obtener todos los eventos',
        auth: true
      },
      {
        name: 'Crear Evento',
        method: 'POST',
        path: '/api/events',
        description: 'Crear nuevo evento',
        auth: true,
        body: {
          title: 'string',
          description: 'string',
          date: 'string (YYYY-MM-DD)',
          time: 'string (HH:MM)',
          capacity: 'number',
          price: 'number',
          image_url: 'string (opcional)'
        }
      },
      {
        name: 'Obtener Evento',
        method: 'GET',
        path: '/api/events/{event_id}',
        description: 'Obtener detalle del evento',
        auth: true
      },
      {
        name: 'Actualizar Evento',
        method: 'PATCH',
        path: '/api/events/{event_id}',
        description: 'Actualizar evento',
        auth: true
      },
      {
        name: 'Eliminar Evento',
        method: 'DELETE',
        path: '/api/events/{event_id}',
        description: 'Eliminar evento',
        auth: true
      },
      {
        name: 'Vender Boleto',
        method: 'POST',
        path: '/api/events/{event_id}/tickets',
        description: 'Vender boleto para evento',
        auth: true,
        body: {
          buyer_name: 'string',
          buyer_email: 'string',
          quantity: 'number'
        }
      },
      {
        name: 'Listar Boletos',
        method: 'GET',
        path: '/api/events/{event_id}/tickets',
        description: 'Obtener boletos vendidos',
        auth: true
      },
      {
        name: 'Check-in con Código',
        method: 'POST',
        path: '/api/events/tickets/{code}/checkin',
        description: 'Hacer check-in con código de boleto',
        auth: true
      }
    ]
  },
  {
    category: 'Marketing',
    icon: Megaphone,
    color: 'bg-indigo-500',
    description: 'Códigos promocionales y campañas',
    endpoints: [
      {
        name: 'Listar Códigos Promo',
        method: 'GET',
        path: '/api/marketing/promo-codes',
        description: 'Obtener códigos promocionales',
        auth: true
      },
      {
        name: 'Crear Código Promo',
        method: 'POST',
        path: '/api/marketing/promo-codes',
        description: 'Crear código promocional',
        auth: true,
        body: {
          code: 'string',
          discount_type: 'string (percentage/fixed)',
          discount_value: 'number',
          valid_from: 'string',
          valid_until: 'string',
          max_uses: 'number'
        }
      },
      {
        name: 'Actualizar Código',
        method: 'PATCH',
        path: '/api/marketing/promo-codes/{code_id}',
        description: 'Actualizar código promocional',
        auth: true
      },
      {
        name: 'Eliminar Código',
        method: 'DELETE',
        path: '/api/marketing/promo-codes/{code_id}',
        description: 'Eliminar código promocional',
        auth: true
      },
      {
        name: 'Validar Código',
        method: 'POST',
        path: '/api/marketing/promo-codes/validate',
        description: 'Validar si un código es válido (público)',
        auth: false,
        body: {
          code: 'string',
          restaurant_id: 'string'
        }
      }
    ]
  },
  {
    category: 'Analíticas',
    icon: BarChart3,
    color: 'bg-teal-500',
    description: 'Reportes y estadísticas',
    endpoints: [
      {
        name: 'Dashboard Overview',
        method: 'GET',
        path: '/api/analytics/overview',
        description: 'Obtener resumen del dashboard',
        auth: true
      },
      {
        name: 'Estadísticas Diarias',
        method: 'GET',
        path: '/api/analytics/daily',
        description: 'Estadísticas por día',
        auth: true,
        queryParams: {
          date: 'string (YYYY-MM-DD)'
        }
      },
      {
        name: 'Estadísticas Mensuales',
        method: 'GET',
        path: '/api/analytics/monthly',
        description: 'Estadísticas del mes',
        auth: true,
        queryParams: {
          month: 'number',
          year: 'number'
        }
      }
    ]
  },
  {
    category: 'Predicciones ML',
    icon: TrendingDown,
    color: 'bg-violet-500',
    description: 'Predicción de no-shows con Machine Learning',
    endpoints: [
      {
        name: 'Predicción Individual',
        method: 'GET',
        path: '/api/predictions/no-show/{reservation_id}',
        description: 'Obtener predicción de no-show para una reservación',
        auth: true,
        response: {
          risk_score: 'number (0-1)',
          risk_level: 'string (minimal/low/medium/high)',
          factors: 'object',
          recommendations: 'array'
        }
      },
      {
        name: 'Reservaciones Alto Riesgo',
        method: 'GET',
        path: '/api/predictions/high-risk',
        description: 'Listar reservaciones de alto riesgo',
        auth: true
      },
      {
        name: 'Predicciones en Lote',
        method: 'POST',
        path: '/api/predictions/batch',
        description: 'Obtener predicciones para múltiples reservaciones',
        auth: true,
        body: {
          reservation_ids: 'array de strings'
        }
      },
      {
        name: 'Estadísticas No-Shows',
        method: 'GET',
        path: '/api/predictions/stats',
        description: 'Estadísticas históricas de no-shows',
        auth: true
      }
    ]
  },
  {
    category: 'Cumpleaños',
    icon: Cake,
    color: 'bg-rose-500',
    description: 'Sistema de mensajes automáticos de cumpleaños',
    endpoints: [
      {
        name: 'Obtener Configuración',
        method: 'GET',
        path: '/api/birthday/settings',
        description: 'Obtener configuración de cumpleaños',
        auth: true
      },
      {
        name: 'Actualizar Configuración',
        method: 'PUT',
        path: '/api/birthday/settings',
        description: 'Actualizar configuración',
        auth: true,
        body: {
          enabled: 'boolean',
          channels: 'array (email/sms/whatsapp)',
          timing: 'string (week_before/day_before/same_day)',
          message_template: 'string',
          coupon_enabled: 'boolean',
          coupon_value: 'number'
        }
      },
      {
        name: 'Próximos Cumpleaños',
        method: 'GET',
        path: '/api/birthday/upcoming',
        description: 'Listar próximos cumpleaños',
        auth: true,
        queryParams: {
          days: 'number'
        }
      },
      {
        name: 'Enviar Mensaje de Prueba',
        method: 'POST',
        path: '/api/birthday/send-test/{customer_id}',
        description: 'Enviar mensaje de prueba a cliente',
        auth: true
      },
      {
        name: 'Procesar Cumpleaños',
        method: 'POST',
        path: '/api/birthday/process',
        description: 'Procesar y enviar mensajes del día',
        auth: true
      },
      {
        name: 'Historial de Mensajes',
        method: 'GET',
        path: '/api/birthday/history',
        description: 'Ver historial de mensajes enviados',
        auth: true
      }
    ]
  },
  {
    category: 'Webhooks',
    icon: Webhook,
    color: 'bg-slate-500',
    description: 'Gestión de webhooks para integraciones',
    endpoints: [
      {
        name: 'Listar Eventos Disponibles',
        method: 'GET',
        path: '/api/webhooks/events',
        description: 'Obtener tipos de eventos disponibles',
        auth: false
      },
      {
        name: 'Listar Webhooks',
        method: 'GET',
        path: '/api/webhooks',
        description: 'Obtener webhooks configurados',
        auth: true
      },
      {
        name: 'Crear Webhook',
        method: 'POST',
        path: '/api/webhooks',
        description: 'Crear nuevo webhook',
        auth: true,
        body: {
          url: 'string',
          events: 'array de strings',
          description: 'string (opcional)'
        },
        response: {
          id: 'string',
          secret: 'string (guardar de forma segura)'
        }
      },
      {
        name: 'Obtener Webhook',
        method: 'GET',
        path: '/api/webhooks/{webhook_id}',
        description: 'Obtener detalle del webhook',
        auth: true
      },
      {
        name: 'Actualizar Webhook',
        method: 'PATCH',
        path: '/api/webhooks/{webhook_id}',
        description: 'Actualizar webhook',
        auth: true
      },
      {
        name: 'Eliminar Webhook',
        method: 'DELETE',
        path: '/api/webhooks/{webhook_id}',
        description: 'Eliminar webhook',
        auth: true
      },
      {
        name: 'Probar Webhook',
        method: 'POST',
        path: '/api/webhooks/{webhook_id}/test',
        description: 'Enviar evento de prueba',
        auth: true
      },
      {
        name: 'Regenerar Secreto',
        method: 'POST',
        path: '/api/webhooks/{webhook_id}/regenerate-secret',
        description: 'Generar nuevo secreto de firma',
        auth: true
      },
      {
        name: 'Ver Logs de Entregas',
        method: 'GET',
        path: '/api/webhooks/{webhook_id}/logs',
        description: 'Ver historial de entregas',
        auth: true
      }
    ]
  },
  {
    category: 'Widget Público',
    icon: Globe,
    color: 'bg-emerald-500',
    description: 'Endpoints públicos para widgets embebidos',
    endpoints: [
      {
        name: 'Info del Restaurante',
        method: 'GET',
        path: '/api/public/restaurant/{restaurant_id}',
        description: 'Obtener información pública del restaurante',
        auth: false
      },
      {
        name: 'Consultar Disponibilidad',
        method: 'GET',
        path: '/api/public/availability/{restaurant_id}',
        description: 'Verificar disponibilidad',
        auth: false,
        queryParams: {
          date: 'string (YYYY-MM-DD)',
          party_size: 'number'
        }
      },
      {
        name: 'Crear Reservación Pública',
        method: 'POST',
        path: '/api/public/reservation/{restaurant_id}',
        description: 'Crear reservación desde widget',
        auth: false,
        body: {
          guest_name: 'string',
          guest_email: 'string',
          guest_phone: 'string',
          party_size: 'number',
          date: 'string',
          time: 'string'
        }
      },
      {
        name: 'Eventos Públicos',
        method: 'GET',
        path: '/api/public/events/restaurant/{restaurant_id}',
        description: 'Listar eventos públicos del restaurante',
        auth: false
      },
      {
        name: 'Evento por Slug',
        method: 'GET',
        path: '/api/public/event/{slug}',
        description: 'Obtener evento por su slug URL',
        auth: false
      },
      {
        name: 'Registro Lista de Espera',
        method: 'POST',
        path: '/api/public/waitlist/{restaurant_id}',
        description: 'Registro público en lista de espera',
        auth: false,
        body: {
          name: 'string',
          phone: 'string',
          party_size: 'number'
        }
      }
    ]
  }
];

// Webhook events for Bubble.io
const webhookEvents = [
  { event: 'reservation.created', desc: 'Nueva reservación creada' },
  { event: 'reservation.updated', desc: 'Reservación actualizada' },
  { event: 'reservation.cancelled', desc: 'Reservación cancelada' },
  { event: 'reservation.confirmed', desc: 'Reservación confirmada' },
  { event: 'reservation.seated', desc: 'Cliente sentado' },
  { event: 'reservation.completed', desc: 'Servicio completado' },
  { event: 'reservation.noshow', desc: 'Cliente no se presentó' },
  { event: 'customer.created', desc: 'Nuevo cliente registrado' },
  { event: 'customer.updated', desc: 'Cliente actualizado' },
  { event: 'waitlist.added', desc: 'Agregado a lista de espera' },
  { event: 'waitlist.notified', desc: 'Notificado de disponibilidad' },
  { event: 'waitlist.converted', desc: 'Convertido a reservación' },
  { event: 'event.created', desc: 'Nuevo evento creado' },
  { event: 'event.updated', desc: 'Evento actualizado' },
  { event: 'event.ticket_sold', desc: 'Boleto vendido' },
  { event: 'table.status_changed', desc: 'Estado de mesa cambiado' },
];

const methodColors = {
  GET: 'bg-green-500/10 text-green-600 border-green-500/20',
  POST: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  PATCH: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  PUT: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  DELETE: 'bg-red-500/10 text-red-600 border-red-500/20',
};

export default function AdminBubbleIO() {
  const [copiedText, setCopiedText] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showWebhookDialog, setShowWebhookDialog] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookDescription, setWebhookDescription] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data);
      }
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopiedText(null), 2000);
  };

  const copyEndpointForBubble = (endpoint, category) => {
    const bubbleConfig = `
API Call Name: ${endpoint.name}
Method: ${endpoint.method}
URL: ${API_URL}${endpoint.path}
${endpoint.auth ? 'Headers: Authorization = Bearer [token]' : 'No authentication required'}
${endpoint.body ? `Body (JSON): ${JSON.stringify(endpoint.body, null, 2)}` : ''}
${endpoint.queryParams ? `Query Parameters: ${JSON.stringify(endpoint.queryParams, null, 2)}` : ''}
    `.trim();
    
    copyToClipboard(bubbleConfig, `${category}-${endpoint.name}`);
  };

  const handleCreateWebhook = async () => {
    if (!webhookUrl || selectedEvents.length === 0) {
      toast.error('URL y al menos un evento son requeridos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url: webhookUrl,
          events: selectedEvents,
          description: webhookDescription || 'Webhook para Bubble.io',
          is_active: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Webhook creado exitosamente');
        setShowWebhookDialog(false);
        setWebhookUrl('');
        setWebhookDescription('');
        setSelectedEvents([]);
        fetchWebhooks();
        
        // Show the secret
        toast.info(`Secreto: ${data.secret}`, { duration: 10000 });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear webhook');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (!confirm('¿Eliminar este webhook?')) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/webhooks/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Webhook eliminado');
      fetchWebhooks();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleTestWebhook = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks/${id}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        toast.success('Webhook probado exitosamente');
      } else {
        toast.error(`Error: ${result.error || result.status_code}`);
      }
    } catch (error) {
      toast.error('Error al probar webhook');
    }
  };

  const toggleEvent = (event) => {
    setSelectedEvents(prev => 
      prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]
    );
  };

  const downloadBubbleGuide = () => {
    let guide = `# OkReserva API - Guía de Integración para Bubble.io

## Base URL
${API_URL}/api

## Autenticación
Todos los endpoints protegidos requieren el header:
Authorization: Bearer <token>

Para obtener el token, usa el endpoint de login.

---

`;
    
    bubbleEndpoints.forEach(category => {
      guide += `## ${category.category}\n${category.description}\n\n`;
      
      category.endpoints.forEach(ep => {
        guide += `### ${ep.name}\n`;
        guide += `- **Método:** ${ep.method}\n`;
        guide += `- **URL:** ${API_URL}${ep.path}\n`;
        guide += `- **Autenticación:** ${ep.auth ? 'Requerida' : 'No requerida'}\n`;
        
        if (ep.body) {
          guide += `- **Body:**\n\`\`\`json\n${JSON.stringify(ep.body, null, 2)}\n\`\`\`\n`;
        }
        if (ep.queryParams) {
          guide += `- **Query Params:**\n\`\`\`json\n${JSON.stringify(ep.queryParams, null, 2)}\n\`\`\`\n`;
        }
        if (ep.response) {
          guide += `- **Response:**\n\`\`\`json\n${JSON.stringify(ep.response, null, 2)}\n\`\`\`\n`;
        }
        guide += '\n';
      });
      guide += '---\n\n';
    });

    guide += `## Webhooks para Bubble.io

Para recibir eventos en tiempo real en tu app de Bubble.io:

1. Crea un endpoint de tipo "Backend Workflow" en Bubble
2. Configura el webhook en OkReserva con la URL de tu endpoint
3. Guarda el secreto para verificar las firmas

### Eventos Disponibles:
${webhookEvents.map(e => `- ${e.event}: ${e.desc}`).join('\n')}

### Verificación de Firma:
Header: X-OkReserva-Signature
Algoritmo: HMAC-SHA256
`;

    const blob = new Blob([guide], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'okreserva-bubble-integration-guide.md';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Guía descargada');
  };

  const filteredCategories = selectedCategory === 'all' 
    ? bubbleEndpoints 
    : bubbleEndpoints.filter(c => c.category === selectedCategory);

  return (
    <div className="min-h-screen" data-testid="admin-bubble-page">
      <header className="sticky top-0 z-30 h-14 glass flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <img src="https://s3.amazonaws.com/appforest_uf/f1542037825580x604474375034491800/bubble-logo-vector.svg" alt="Bubble.io" className="h-6" onError={(e) => e.target.style.display='none'} />
          <h1 className="text-lg font-semibold">Integración Bubble.io</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={downloadBubbleGuide}>
            <Download className="h-4 w-4 mr-1" /> Descargar Guía
          </Button>
          <Button size="sm" onClick={() => setShowWebhookDialog(true)}>
            <Webhook className="h-4 w-4 mr-1" /> Crear Webhook
          </Button>
        </div>
      </header>

      <div className="p-6 space-y-6 fade-in">
        {/* Quick Start */}
        <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-blue-500/20">
                <BookOpen className="h-6 w-6 text-blue-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">Guía Rápida de Integración</h3>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 rounded-lg bg-background/50">
                    <p className="font-medium mb-1">1. Configurar API Connector</p>
                    <p className="text-muted-foreground">Plugins → API Connector → Add API</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background/50">
                    <p className="font-medium mb-1">2. Base URL</p>
                    <code className="text-xs text-primary">{API_URL}/api</code>
                  </div>
                  <div className="p-3 rounded-lg bg-background/50">
                    <p className="font-medium mb-1">3. Authentication</p>
                    <p className="text-muted-foreground">Header: Authorization = Bearer [token]</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="endpoints" className="space-y-4">
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="endpoints">Endpoints API</TabsTrigger>
            <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
            <TabsTrigger value="examples">Ejemplos</TabsTrigger>
          </TabsList>

          {/* Endpoints Tab */}
          <TabsContent value="endpoints" className="space-y-4">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('all')}
              >
                Todos
              </Button>
              {bubbleEndpoints.map(cat => {
                const Icon = cat.icon;
                return (
                  <Button
                    key={cat.category}
                    size="sm"
                    variant={selectedCategory === cat.category ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory(cat.category)}
                    className="gap-1"
                  >
                    <Icon className="h-3 w-3" />
                    {cat.category}
                  </Button>
                );
              })}
            </div>

            {/* Endpoint Categories */}
            <div className="space-y-4">
              {filteredCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <Card key={category.category}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${category.color}/10`}>
                          <Icon className={`h-5 w-5 ${category.color.replace('bg-', 'text-')}`} />
                        </div>
                        <div>
                          <CardTitle className="text-base">{category.category}</CardTitle>
                          <CardDescription className="text-xs">{category.description}</CardDescription>
                        </div>
                        <Badge variant="outline" className="ml-auto">{category.endpoints.length} endpoints</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {category.endpoints.map((ep) => (
                          <div key={`${ep.method}-${ep.path}`} className="border rounded-lg overflow-hidden">
                            <div 
                              className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer"
                              onClick={() => setExpandedEndpoint(expandedEndpoint === `${category.category}-${ep.path}` ? null : `${category.category}-${ep.path}`)}
                            >
                              <Badge className={`font-mono text-xs px-2 ${methodColors[ep.method]}`}>
                                {ep.method}
                              </Badge>
                              <code className="text-sm flex-1">{ep.path}</code>
                              <span className="text-xs text-muted-foreground hidden sm:block">{ep.name}</span>
                              {ep.auth ? (
                                <Badge variant="outline" className="text-xs">Auth</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs bg-green-500/10">Público</Badge>
                              )}
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyEndpointForBubble(ep, category.category);
                                }}
                              >
                                {copiedText === `${category.category}-${ep.name}` ? (
                                  <Check className="h-3 w-3 text-green-500" />
                                ) : (
                                  <Copy className="h-3 w-3" />
                                )}
                              </Button>
                              <ChevronRight className={`h-4 w-4 transition-transform ${expandedEndpoint === `${category.category}-${ep.path}` ? 'rotate-90' : ''}`} />
                            </div>
                            
                            {expandedEndpoint === `${category.category}-${ep.path}` && (
                              <div className="p-3 bg-muted/30 border-t space-y-3">
                                <p className="text-sm text-muted-foreground">{ep.description}</p>
                                
                                {ep.body && (
                                  <div>
                                    <p className="text-xs font-medium mb-1">Body (JSON):</p>
                                    <pre className="p-2 bg-background rounded text-xs overflow-x-auto">
                                      {JSON.stringify(ep.body, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                
                                {ep.queryParams && (
                                  <div>
                                    <p className="text-xs font-medium mb-1">Query Parameters:</p>
                                    <pre className="p-2 bg-background rounded text-xs overflow-x-auto">
                                      {JSON.stringify(ep.queryParams, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                
                                {ep.response && (
                                  <div>
                                    <p className="text-xs font-medium mb-1">Response:</p>
                                    <pre className="p-2 bg-background rounded text-xs overflow-x-auto">
                                      {JSON.stringify(ep.response, null, 2)}
                                    </pre>
                                  </div>
                                )}

                                <div className="pt-2">
                                  <p className="text-xs font-medium mb-1">Configuración para Bubble.io:</p>
                                  <pre className="p-2 bg-background rounded text-xs overflow-x-auto">
{`API Call: ${ep.name}
Method: ${ep.method}
URL: ${API_URL}${ep.path}
${ep.auth ? 'Header: Authorization = Bearer [Saved token]' : ''}`}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-5 w-5 text-yellow-500" />
                      Webhooks para Bubble.io
                    </CardTitle>
                    <CardDescription>
                      Configura webhooks para recibir eventos en tiempo real en tu app de Bubble
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowWebhookDialog(true)}>
                    <Plus className="h-4 w-4 mr-1" /> Nuevo Webhook
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {webhooks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Webhook className="h-12 w-12 mx-auto mb-2 opacity-20" />
                    <p>No hay webhooks configurados</p>
                    <p className="text-xs mt-1">Crea un webhook para recibir eventos en tu app de Bubble.io</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {webhooks.map((wh) => (
                      <div key={wh.id} className="p-4 rounded-lg border bg-card">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {wh.is_active ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-muted-foreground" />
                              )}
                              <code className="text-sm truncate">{wh.url}</code>
                            </div>
                            {wh.description && (
                              <p className="text-xs text-muted-foreground mb-2">{wh.description}</p>
                            )}
                            <div className="flex flex-wrap gap-1 mb-2">
                              {wh.events.slice(0, 4).map((ev, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{ev}</Badge>
                              ))}
                              {wh.events.length > 4 && (
                                <Badge variant="outline" className="text-xs">+{wh.events.length - 4}</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3 text-green-500" /> {wh.success_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <XCircle className="h-3 w-3 text-red-500" /> {wh.failure_count}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleTestWebhook(wh.id)} title="Probar">
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDeleteWebhook(wh.id)} title="Eliminar">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Eventos Disponibles</CardTitle>
                <CardDescription>Eventos que puedes recibir en tu app de Bubble.io</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {webhookEvents.map((ev, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                      <code className="text-xs text-primary font-mono">{ev.event}</code>
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{ev.desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Configuración en Bubble.io</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h4 className="font-medium mb-2">1. Crear Backend Workflow</h4>
                    <p className="text-sm text-muted-foreground">
                      Ve a Settings → API → Enable Workflow API and backend workflows
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h4 className="font-medium mb-2">2. Crear Endpoint</h4>
                    <p className="text-sm text-muted-foreground">
                      Backend Workflows → New API workflow → Define parameters para recibir el payload
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h4 className="font-medium mb-2">3. URL del Webhook</h4>
                    <pre className="mt-2 p-2 bg-background rounded text-xs overflow-x-auto">
{`https://tu-app.bubbleapps.io/version-test/api/1.1/wf/tu-webhook`}
                    </pre>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h4 className="font-medium mb-2">4. Verificar Firma (Opcional)</h4>
                    <pre className="mt-2 p-2 bg-background rounded text-xs overflow-x-auto">
{`Header: X-OkReserva-Signature
Formato: sha256=<hmac_signature>

// Verificar en Bubble con plugin de crypto
// HMAC-SHA256(payload, secret)`}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Examples Tab */}
          <TabsContent value="examples" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Ejemplo: Autenticación</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="p-4 bg-muted/50 rounded-lg text-xs overflow-x-auto">
{`// En Bubble.io API Connector:

API Name: OkReserva
Authentication: None (bearer en header)

// Call: Login
Method: POST
URL: ${API_URL}/api/auth/login/pin
Body type: JSON
Body: {
  "restaurant_name": "<Input Restaurant Name's value>",
  "pin": "<Input PIN's value>"
}

// Guardar el token en el usuario actual
Set state: Current User's token = Result's access_token`}
                </pre>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Ejemplo: Crear Reservación</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="p-4 bg-muted/50 rounded-lg text-xs overflow-x-auto">
{`// Call: Create Reservation
Method: POST
URL: ${API_URL}/api/reservations
Headers:
  - Authorization: Bearer <Current User's token>
  - Content-Type: application/json

Body: {
  "guest_name": "<Input Name's value>",
  "guest_email": "<Input Email's value>",
  "guest_phone": "<Input Phone's value>",
  "party_size": <Input Party Size's value>,
  "date": "<Date Picker's value:formatted as YYYY-MM-DD>",
  "time": "<Dropdown Time's value>",
  "notes": "<Input Notes's value>"
}`}
                </pre>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Ejemplo: Recibir Webhook</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="p-4 bg-muted/50 rounded-lg text-xs overflow-x-auto">
{`// Backend Workflow en Bubble.io

Workflow Name: okreserva_webhook
API Workflow type: Detect request data

// Parámetros a definir:
- event (text)
- timestamp (text)
- data (text - JSON string)

// Ejemplo de payload recibido:
{
  "event": "reservation.created",
  "timestamp": "2026-03-07T12:00:00Z",
  "data": {
    "id": "res_abc123",
    "guest_name": "Juan Pérez",
    "party_size": 4,
    "date": "2026-03-15",
    "time": "20:00",
    "status": "pending"
  }
}

// En el workflow:
1. Parsear data con :extract with Regex o JSON plugin
2. Crear/actualizar registros en tu base de datos
3. Enviar notificaciones si es necesario`}
                </pre>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Webhook Dialog */}
      <Dialog open={showWebhookDialog} onOpenChange={setShowWebhookDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Webhook className="h-5 w-5" />
              Crear Webhook para Bubble.io
            </DialogTitle>
            <DialogDescription>
              Configura un webhook para recibir eventos en tu aplicación de Bubble.io
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>URL del Webhook en Bubble.io *</Label>
              <Input
                placeholder="https://tu-app.bubbleapps.io/version-test/api/1.1/wf/webhook"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Obtén esta URL de tu Backend Workflow en Bubble.io
              </p>
            </div>

            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Input
                placeholder="Ej: Webhook principal para sincronización"
                value={webhookDescription}
                onChange={(e) => setWebhookDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Eventos a recibir *</Label>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
                {webhookEvents.map((ev) => (
                  <div
                    key={ev.event}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      selectedEvents.includes(ev.event)
                        ? 'bg-primary/10 border border-primary'
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                    onClick={() => toggleEvent(ev.event)}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      selectedEvents.includes(ev.event) ? 'bg-primary border-primary' : ''
                    }`}>
                      {selectedEvents.includes(ev.event) && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-mono truncate">{ev.event}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedEvents.length} evento(s) seleccionado(s)
              </p>
            </div>

            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                <strong>Importante:</strong> Guarda el secreto que se generará. Lo necesitarás para verificar las firmas de los webhooks.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWebhookDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateWebhook} disabled={!webhookUrl || selectedEvents.length === 0}>
              Crear Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
